import { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { alarmService } from '../services/alarmService';
import type { Alarm } from '../services/alarmService';

function normalizeError(error: unknown, fallbackMessage: string): Error {
  if (error instanceof Error) return error;
  if (typeof error === 'string') return new Error(error);
  return new Error(fallbackMessage);
}

function safelyUnsubscribe(unsubscribe: () => void, label: string) {
  try {
    // Supabase cleanup functions are typed as void by the services, but may
    // return a promise at runtime. Observe it so a failed cleanup is handled.
    const result = (unsubscribe as unknown as () => unknown)();
    void Promise.resolve(result).catch((error) => {
      console.error(`${label}:`, normalizeError(error, 'Subscription cleanup failed').message);
    });
  } catch (error) {
    console.error(`${label}:`, normalizeError(error, 'Subscription cleanup failed').message);
  }
}

export function useAlarms() {
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [activeAlarms, setActiveAlarms] = useState<Alarm[]>([]);
  const [newAlarms, setNewAlarms] = useState<Alarm[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const mountedAt = useRef<number>(Date.now());

  // Keep the initial loading state active through auth and profile resolution;
  // the alarm load below completes it once a company is available.
  useEffect(() => {
    let cancelled = false;

    const resolveCompany = async () => {
      try {
        const { data: { user }, error: authError } = await supabase.auth.getUser();
        if (authError) throw authError;

        if (!user) {
          if (!cancelled) setIsLoading(false);
          return;
        }

        const { data, error: profileError } = await supabase
          .from('profiles')
          .select('company_id')
          .eq('id', user.id)
          .single();
        if (profileError) throw profileError;

        const resolvedCompanyId = (data as { company_id?: string | null } | null)?.company_id;
        if (!resolvedCompanyId) throw new Error('The current profile is not linked to a company.');

        if (!cancelled) {
          setError(null);
          setCompanyId(resolvedCompanyId);
        }
      } catch (cause) {
        if (cancelled) return;
        const nextError = normalizeError(cause, 'Could not resolve the current company.');
        console.error('useAlarms: company resolution error:', nextError.message);
        setError(nextError);
        setIsLoading(false);
      }
    };

    void resolveCompany();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    let pendingLoads = 0;

    const load = async () => {
      if (cancelled) return;
      pendingLoads += 1;
      setIsLoading(true);
      setError(null);

      try {
        const data = await alarmService.fetchActiveAlarms(companyId);
        if (!cancelled) setActiveAlarms(data);
      } catch (cause) {
        if (!cancelled) {
          const nextError = normalizeError(cause, 'Could not load active alarms.');
          console.error('useAlarms: error loading alarms:', nextError.message);
          setError(nextError);
          // Keep the most recent successful alarm list visible.
        }
      } finally {
        pendingLoads -= 1;
        if (!cancelled && pendingLoads === 0) setIsLoading(false);
      }
    };

    void load();

    const unsubscribe = alarmService.subscribeToAlarms(companyId, (payload) => {
      if (cancelled) return;
      if (payload.eventType === 'INSERT') {
        const newRecord = payload.new as Record<string, unknown>;
        const triggeredAt = new Date(newRecord['triggered_at'] as string).getTime();
        // Only pop a notification for alarms triggered after this tab loaded
        if (triggeredAt >= mountedAt.current) {
          setNewAlarms((prev) => [...prev, newRecord as unknown as Alarm]);
        }
      }
      // Always refresh the active alarm list
      void load();
    });

    return () => {
      cancelled = true;
      safelyUnsubscribe(unsubscribe, 'useAlarms: unsubscribe error');
    };
  }, [companyId]);

  const dismissNewAlarm = (alarmId: string) => {
    setNewAlarms((prev) => prev.filter((a) => a.id !== alarmId));
  };

  const acknowledgeAlarm = async (alarmId: string) => {
    try {
      await alarmService.acknowledgeAlarm(alarmId);
      setError(null);
    } catch (cause) {
      const nextError = normalizeError(cause, 'Could not acknowledge the alarm.');
      console.error('useAlarms: acknowledge error:', nextError.message);
      setError(nextError);
    }
    dismissNewAlarm(alarmId);
  };

  const resolveAlarm = async (alarmId: string) => {
    try {
      await alarmService.resolveAlarm(alarmId);
      setError(null);
    } catch (cause) {
      const nextError = normalizeError(cause, 'Could not resolve the alarm.');
      console.error('useAlarms: resolve error:', nextError.message);
      setError(nextError);
    }
    dismissNewAlarm(alarmId);
  };

  return {
    activeAlarms,
    newAlarms,
    dismissNewAlarm,
    acknowledgeAlarm,
    resolveAlarm,
    isLoading,
    error,
  };
}
